/* eslint-disable  no-underscore-dangle */
import { useMemo } from 'react';
import _ from 'lodash';

import { useDataService } from './ApiDataService';

const BASE_URL = '/query/filter';
const TEST_TYPE_URL = '/ipm/v1/open/testTypes';

export const ENTITY_TYPES = {
    SITE: 'site',
    BUSINESS_SERVICE: 'businessservice',
    ACCESS_POINT: 'accesspoint',
    AGENT: 'agent',
    HYPERVISOR: 'hypervisor',
    INTERFACE: 'interface',
    DEVICE: 'device',
    RADIO: 'radio',
    SERVER: 'server',
    TEST: 'test',
    TEST_TYPE: 'testtype',
    VIRTUAL_MACHINE: 'virtualmachine',
    WIRELESS_CONTROLLER: 'wirelesscontroller',
};

function getEntityTypeUrl(entityType) {
    if (entityType === ENTITY_TYPES.TEST_TYPE) return TEST_TYPE_URL;
    return `${BASE_URL}/${entityType}`;
}

function getEntityTypeParams(entityType, params) {
    if (entityType === ENTITY_TYPES.TEST_TYPE) {
        return {
            sort: 'details.title',
            ...params,
            searchFields: ['details.title'],
            query: JSON.stringify({
                hidden: false,
            }),
        };
    }

    return params;
}

export function getEntityTitle(entity) {
    const name = entity?.name || entity?.ipAddress;
    const ipAddress = name !== entity?.ipAddress ? entity?.ipAddress : null;
    return ipAddress ? `${name} (${ipAddress})` : name;
}

/**
 * Some entities don't have a `type` property, which is needed by the entity list filter, and is also generally useful.
 * This function adds a `type` property to an entity that doesn't have one.
 *
 * @param {string} type The type of entity and the value that will be added to its `type` property.
 * @param {object} entity The entity to add the `type` property to.
 *
 * @returns {object} The entity.
 */
function ensureType(type, entity) {
    return _.defaults(entity, {
        type,
    });
}

/**
 * Some entities don't have a `uuid` property (i.e. TestType).  This normalizes any
 *  entities that do not have that property by copying thier `_id` property to the
 * `uuid` property to keep them consistent.
 *
 * @param {object} entity The entity to add the `uuid` property to.
 *
 * @returns {object} The entity.
 */
export function ensureUuid(entity = {}) {
    if (!entity.uuid) {
        return _.defaults(entity, {
            uuid: entity._id,
        });
    }

    return entity;
}

/**
 * The Filter API isn't standardized, and we use an entirely different API to get test types.
 * This function normalizes the response from the various APIs to have a `data` property that
 * contains the entities, and a `total` property that has the total count so that paging works.
 */
function normalizeListResponse(entityType, response) {
    const updater = _.flow(_.partial(ensureType, entityType), ensureUuid);
    const entities = response?.[`${entityType}s`] || response?.data || response;
    return {
        data: entities.map?.(updater).filter((entity) => !entity.hidden) || [],
        total: response?.total || response?.count || 0,
    };
}

/**
 * Loads entities for the entity list filter.
 */
export default function EntityServiceFactory(dataService) {
    function getEntity(entityType, entityUuid) {
        const entityTypeUrl = getEntityTypeUrl(entityType);
        const updater = _.flow(_.partial(ensureType, entityType), ensureUuid);
        return dataService.get(`${entityTypeUrl}/${entityUuid}`).then(updater);
    }

    function getEntities(entityType, params = {}) {
        return dataService
            .get(getEntityTypeUrl(entityType), getEntityTypeParams(entityType, params))
            .then((response) => normalizeListResponse(entityType, response));
    }

    return {
        getEntity,
        getEntities,
    };
}

export function useEntityService() {
    const dataService = useDataService();
    return useMemo(() => EntityServiceFactory(dataService), [dataService]);
}
